import React, { isValidElement, useMemo } from 'react';
import clsx from 'clsx';
import { AriaButtonProps } from 'react-aria/useButton';
import { FocusRing } from 'react-aria/FocusRing';
import { useHover } from 'react-aria/useHover';
import { useMessageFormatter } from '@react-aria/i18n';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import { mergeProps } from 'react-aria/mergeProps';
import { useFocusableRef } from '@react-spectrum/utils';
import { FocusableRef, LinkDOMProps, PressEvents } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/iconButton/iconButton.module.css';

import intlMessages from '../i18n';
import { Icon } from '../../Icon';
import { Spinner } from '../../Spinner';
import {
  SapphireStyleProps,
  useButton,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import {
  BaseComponentKeys,
  GlobalDomAttributes,
  LinkDOMPropsKeys,
} from '../../utils/types';

export type IconButtonProps<T extends 'a' | 'button'> = Omit<
  Pick<
    AriaButtonProps<T>,
    | BaseComponentKeys
    | 'autoFocus'
    | 'onFocus'
    | 'onBlur'
    | 'isDisabled'
    | 'elementType'
    | 'excludeFromTabOrder'
    | 'type'
  >,
  'aria-label'
> &
  Pick<LinkDOMProps, LinkDOMPropsKeys> &
  SapphireStyleProps &
  GlobalDomAttributes &
  PressEvents & {
    /**
     * The content of the button. This should be an svg icon.
     * Usually imports from `@danske/sapphire-icons`.
     *
     */
    children: React.ReactNode;

    /**
     * @default 'md'
     */
    size?: 'xs' | 'sm' | 'md' | 'lg';

    /**
     * @default false
     */
    isLoading?: boolean;

    /**
     * The visual appearance of the button.
     * @default 'tertiary'
     */
    variant?: IconButtonVariant;

    /**
     * @deprecated
     * Please use onPress instead, which has
     * [several UX
     *   improvements](https://react-spectrum.adobe.com/blog/building-a-button-part-1.html) over
     *   onClick, or onClick if accessing the mouse event is necessary.
     */
    UNSAFE_onClick?: React.MouseEventHandler;

    /**
     * Prevent focus when the button is clicked.
     * Should only be used in special cases. By default, buttons should receive focus when pressed.
     *
     */
    UNSAFE_preventFocusOnPress?: boolean;
  } & (
    | {
        /**
         * The button's label. This is not shown on the screen but it is required
         * for accessibility reasons.
         */
        'aria-label': string;
        'aria-hidden'?: never;
      }
    | {
        'aria-hidden': true | 'true';
        'aria-label'?: never;
      }
  );

export type IconButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'danger'
  | 'danger-secondary'
  | 'danger-tertiary';

export const IconButton = React.forwardRef(function IconButton<
  T extends 'a' | 'button'
>(
  {
    variant = 'tertiary',
    size = 'md',
    isLoading,
    UNSAFE_onClick,
    UNSAFE_preventFocusOnPress,
    onPress: onPressOriginal,
    ...otherProps
  }: IconButtonProps<T>,
  ref: FocusableRef<HTMLButtonElement | HTMLAnchorElement>
) {
  useThemeCheck();

  const { autoFocus, isDisabled } = otherProps;

  const canClick = useMemo(
    () => !isDisabled && !isLoading,
    [isDisabled, isLoading]
  );
  const onPress = useMemo(
    () => (canClick ? onPressOriginal : () => {}),
    [canClick, onPressOriginal]
  );

  const {
    elementType: ElementType = (otherProps.href ? 'a' : 'button') as string,
  } = otherProps;
  const formatMessage = useMessageFormatter(intlMessages);
  const domRef = useFocusableRef<HTMLButtonElement | HTMLAnchorElement>(ref);
  const { buttonProps, isPressed } = useButton(
    {
      ...otherProps,
      onPress,
      elementType: ElementType as 'a' | 'button',
      preventFocusOnPress: UNSAFE_preventFocusOnPress,
    },
    domRef
  );
  const { hoverProps, isHovered } = useHover({ isDisabled });
  const { styleProps } = useSapphireStyleProps(otherProps);

  return (
    <FocusRing focusRingClass={clsx(styles['is-focus'])} autoFocus={autoFocus}>
      <ElementType
        {...mergeProps(
          buttonProps,
          hoverProps,
          {
            // TODO(v6): remove UNSAFE_onClick in favor of onClick that's now available from react-aria directly
            onClick: canClick ? UNSAFE_onClick : undefined,
          },
          filterDOMProps(otherProps, {
            global: true,
            propNames: new Set(['aria-current', 'aria-hidden']),
            isLink: true,
          })
        )}
        ref={domRef}
        aria-disabled={!canClick}
        style={styleProps.style}
        className={clsx(
          styles['sapphire-icon-button'],
          styles[`sapphire-icon-button--${variant}`],
          styles['js-focus'],
          styles['js-hover'],
          {
            [styles['is-active']]: isPressed,
            [styles['is-hover']]: isHovered,
            [styles['is-disabled']]: isDisabled,
            [styles['sapphire-icon-button--xs']]: size === 'xs',
            [styles['sapphire-icon-button--sm']]: size === 'sm',
            [styles['sapphire-icon-button--lg']]: size === 'lg',
            [styles['sapphire-icon-button--loading']]: isLoading,
          },
          styleProps.className
        )}
      >
        {isLoading && (
          <span className={clsx(styles['sapphire-icon-button__spinner'])}>
            <Spinner
              aria-label={formatMessage('loading')}
              variant="inherit"
              size={size === 'sm' ? '3xs' : size === 'md' ? '2xs' : 'xs'}
            />
          </span>
        )}
        <span
          // Fix js hover effect issue
          style={{ pointerEvents: 'none' }}
          className={clsx(styles['sapphire-icon-button__icon'])}
          aria-hidden={isLoading}
        >
          {isValidElement(otherProps.children) ? (
            <Icon size={size === 'xs' ? 'sm' : size}>
              {otherProps.children}
            </Icon>
          ) : (
            otherProps.children
          )}
        </span>
      </ElementType>
    </FocusRing>
  );
});
